// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type EtfFilters } from '@shared/data-access/interfaces/api/ai/etf';
import type { MutualFundFilters } from '@shared/data-access/interfaces/api/ai/mutual-funds';
import {
  type ETF,
  type MutualFund,
  type SearchETFResp,
  type SearchMutualFundsResp,
} from '@shared/data-access/interfaces/api/ai/screener';
import { useDebounce } from '@shared/ui-kit/hooks';
import { useEffect, useState } from 'react';
import { screenerApi } from '@advisor-app/store/api/ai/screener-api';

export type AssetType = 'etf' | 'mutual_fund';

type SearchRespType = ETF[] | MutualFund[];
interface AssetTypeMapping {
  etf: ETF[];
  mutual_fund: MutualFund[];
}

interface PayloadAssetTypeMapping {
  etf: EtfFilters;
  mutual_fund: MutualFundFilters;
}

export const useSearchAsset = <T extends AssetType>(
  searchText: string,
  assetType: T,
  options: Partial<PayloadAssetTypeMapping[T]> = {},
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [assets, setAssets] = useState<SearchRespType>([]);
  const debounceValue = useDebounce(searchText, 300);
  const [searchScreener] = screenerApi.useLazySearchScreenerQuery();

  useEffect(() => {
    const search = async () => {
      if (!searchText) return setAssets([]);
      setIsLoading(true);
      try {
        const payload = {
          text_search: debounceValue,
          ...options,
        } as any;
        const resp = (await searchScreener({ payload, assetType }).unwrap()) as
          | SearchETFResp
          | SearchMutualFundsResp;

        const assets = 'etfs' in resp ? resp.etfs : resp.mfs;
        setAssets(assets);
      } catch {
        setAssets([]);
      } finally {
        setIsLoading(false);
      }
    };
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceValue]);

  return { isLoading, assets: assets as AssetTypeMapping[T] };
};
